import type { PlainDateString } from "@sa-nu/shared-utils/date";
import { PlainDate } from "@sa-nu/shared-utils/date";
import { useCallback, useState } from "react";

import {
  ReservationAgreeButton,
  type ReservationAgreeButtonMessages,
} from "../../ui/buttons/reservation-agree-button";
import { RequiredAnswerSection } from "../required-answer-section";

const FROZEN_PERIOD_START = PlainDate.from("2026-01-28");
const FROZEN_PERIOD_END = PlainDate.from("2026-02-28");

function isWithinFrozenPeriod(firstNight: PlainDateString): boolean {
  const date = PlainDate.from(firstNight);
  return (
    PlainDate.compare(date, FROZEN_PERIOD_START) >= 0 &&
    PlainDate.compare(date, FROZEN_PERIOD_END) <= 0
  );
}

export type Nasu3rdFrozenTermsMessages = {
  title: string;
  paragraph1: string;
  paragraph2: string;
  paragraph3: string;
  agreeButtonLabel: string;
  agreeButtonMessages?: ReservationAgreeButtonMessages;
  requiredLabel?: string;
};

export const JA_MESSAGES: Nasu3rdFrozenTermsMessages = {
  title: "露天風呂のご利用について",
  paragraph1:
    "対象のお部屋では、寒波の発生時に露天風呂の水道管が一時的に凍結し、ご利用いただけない場合があります。 現在、根本的な対策を進めておりますが、完了まではお時間を要する見込みです。",
  paragraph2:
    "恐れ入りますが、上記内容をご理解・ご了承のうえ、ご予約ください。",
  paragraph3:
    "なお、ご滞在中に露天風呂をご利用いただけなかった場合は、カスタマーサポートまでご連絡ください。 状況を確認のうえ、宿泊費・オプション費用返金の補填対応（オーナー様の場合は泊数の返還）をいたします。",
  agreeButtonLabel: "上記内容を了承します",
};

/** 予約の前に露天風呂の凍結への同意が要るか。凍結の期間にチェックインする那須3rd だけ */
export function requiresNasu3rdFrozenTerms(
  siteSlug: string,
  firstNight: PlainDateString,
): boolean {
  return siteSlug === "nasu3rd" && isWithinFrozenPeriod(firstNight);
}

export function useNasu3rdFrozenTerms(
  siteSlug: string,
  firstNight: PlainDateString,
  messages: Nasu3rdFrozenTermsMessages = JA_MESSAGES,
) {
  const [agreed, setAgreed] = useState(false);
  const isRequired = requiresNasu3rdFrozenTerms(siteSlug, firstNight);

  const renderNasu3rdFrozenTerms = useCallback(() => {
    if (!isRequired) {
      return null;
    }
    return (
      <Nasu3rdFrozenTerms
        agreed={agreed}
        setAgreed={setAgreed}
        messages={messages}
      />
    );
  }, [agreed, isRequired, messages]);

  return {
    renderNasu3rdFrozenTerms,
    agreedOrUnnecessary: agreed || !isRequired,
  };
}

export function Nasu3rdFrozenTerms({
  agreed,
  setAgreed,
  messages = JA_MESSAGES,
}: {
  agreed: boolean;
  setAgreed: (value: boolean) => void;
  messages?: Nasu3rdFrozenTermsMessages;
}) {
  return (
    <RequiredAnswerSection
      title={messages.title}
      messages={
        messages.requiredLabel
          ? { requiredLabel: messages.requiredLabel }
          : undefined
      }
      actionArea={
        <div className="flex justify-center">
          <ReservationAgreeButton
            label={messages.agreeButtonLabel}
            required
            checked={agreed}
            onToggle={setAgreed}
            testId="nasu3rd-frozen-terms-agree-button"
            messages={messages.agreeButtonMessages}
          />
        </div>
      }
    >
      <p>{messages.paragraph1}</p>
      <p className="mt-[1em]">{messages.paragraph2}</p>
      <p className="mt-[1em]">{messages.paragraph3}</p>
    </RequiredAnswerSection>
  );
}
